import React, { useEffect, useState } from "react";
import { request } from "../../utils/request";
import { getUser } from "../../utils/user";
import { Container, Title, CenterColumn, LogoFrame } from "./Components";
import Header from "../../components/Header/Header";
import { Modal } from "../../components/Modal/Modal";
import SearchInput from "../../components/SearchInput/SearchInput";
import VaccineTable from "../../components/VaccineTable/VaccineTable";
import { VaccineCodeAccordion } from "../../components/VaccineCodeAccordion";
import moment from "moment-timezone";
import { Box, makeStyles, Typography } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import LinkIcon from "@material-ui/icons/Link";
import { useTranslation } from "react-i18next";
import { io } from "socket.io-client";

import { FaCalendarAlt } from "react-icons/fa";
import { Divider } from "antd";

const useStyles = makeStyles(() => ({
  imgLinkIcon: {
    position: "absolute",
    right: "5px",
    bottom: "0px",
    color: "#7dcef3",
  },

  textUpdatedAt: {
    fontSize: 12,
    color: "#808080",
    marginTop: 10,
  },

  textDate: {
    fontSize: 10,
    color: "#000000",
    fontWeight: "bold",
    marginBottom: 20,
  },
}));

const MyCard = (props) => {
  const user = getUser();
  const classes = useStyles();

  const { t } = useTranslation("vaccinesPage");

  const [clinicVaccines, setClinicVaccines] = useState([]);
  const [userEnterpriseInfo, setUserEnterpriseInfo] = useState([]);

  const [userVaccines, setUserVaccines] = useState(null);
  const [userAntibodies, setUserAntibodies] = useState(null);
  const [optionalVaccines, setOptionalVaccines] = useState(null);
  const [lastVaccineDate, setLastVaccineDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState(null);
  const [clinicUrl, setClinicUrl] = useState(null);
  const [birthday, setBirthday] = useState("");
  const [search, setSearch] = useState("");
  const [isOpenNoVacAdministratedModal, setIsOpenNoVacAdministratedModal] =
    useState(false);
  const [qrcodeStatus, setQrcodeStatus] = useState([]);
  const [vaccinationCampaignsData, setVaccinationCampaignsData] = useState([]);

  const handleChange = (event) => {
    setSearch(event.target.value);
  };

  const handleCloseModal = () => {
    setIsOpenNoVacAdministratedModal(false);
  };

  const handleClickConfirmModal = () => {
    setIsOpenNoVacAdministratedModal(false);
    window.location.replace(
      "/secure/my-health/my-vaccinations-card/upload-vaccinations-card"
    );
  };

  const socket = io("https://api.immunie.net");

  useEffect(() => {
    const listener = (data) => {
      if (data.status === "SUCCESS") {
        setQrcodeStatus((prev) => {
          const newStatus = [
            ...prev.filter((v) => v.id !== data.id),
            { id: data.id, status: "scanned" },
          ];

          return newStatus;
        });

        setTimeout(() => window.location.reload(), 2000);
      }
    };
    socket.on("qr-code", listener);
    return () => socket.off("qr-code", listener);
  }, [setQrcodeStatus]);

  useEffect(() => {
    const getUserVaccines = async () => {
      try {
        const { data: result } = await request.get(
          `/client-app/find-user-vaccines?id=${user.id}&includeAllAges=true`
        );

        const { data: campaigns } = await request.get(
          `/client-app/vaccination-campaign-data`
        );

        if (campaigns.length > 0) {
          setVaccinationCampaignsData(campaigns[0].vaccinationCampaigns);
        }

        const officials = (result.vaccines || []).filter(
          (vaccine) => vaccine.optional === false
        );
        const optionals = (result.vaccines || []).filter(
          (vaccine) => vaccine.optional === true
        );

        setUserVaccines(officials);
        setOptionalVaccines(optionals || []);
        setUserAntibodies(result.antibodies);
        setClinicVaccines(result.clinicVaccines);
        setUserEnterpriseInfo(result.enterpriseInfo);

        const logoUrl =
          (result.userClinic &&
            result.userClinic.logo &&
            result.userClinic.logo.url) ||
          null;

        const clinicUrl = (result.userClinic && result.userClinic.url) || null;

        setLogoUrl(logoUrl);
        setClinicUrl(clinicUrl);
        setBirthday(result.birthday || user.birthday);
      } catch (error) {
        console.error(`[MyCard getUserVaccines] ${error}`);
      } finally {
        setLoading(false);
      }
    };
    getUserVaccines();
  }, []);

  useEffect(() => {
    const calculateLastUpdateDate = () => {
      if (!userVaccines) return;

      let dates = [];

      userVaccines.forEach((vaccine) => {
        if (vaccine.administrated) {
          dates.push(new Date(vaccine.adminDetails.date));
        }
      });

      const lastDate = new Date(Math.max.apply(null, dates));

      const dateFormated =
        (moment(lastDate).isValid() &&
          moment(lastDate).format("YYYY-MM-DD HH:mm:ss")) ||
        "";

      setLastVaccineDate(dateFormated);
    };
    calculateLastUpdateDate();
  }, [userVaccines]);

  return (
    <Container>
      <Header backgroundStyle="ORANGE" />
      <Title>{t("TITLE")}</Title>
      {loading ? (
        <CenterColumn>
          <CircularProgress />
        </CenterColumn>
      ) : (
        <>
          {logoUrl && (
            <LogoFrame backgroundImage={logoUrl}>
              <IconButton href={clinicUrl} className={classes.imgLinkIcon}>
                <LinkIcon />
              </IconButton>
            </LogoFrame>
          )}
          {lastVaccineDate && (
            <>
              <Typography className={`${classes.textUpdatedAt} montserrat`}>
                {t("VACCINATION_CARD_UPDATED_ON")}
              </Typography>
              <Typography className={`${classes.textDate} montserrat`}>
                {moment(lastVaccineDate).format("DD/MM/YYYY - HH:mm")}
              </Typography>
            </>
          )}

          <Box mb="10px" width="100%" display="flex" justifyContent="center">
            <SearchInput
              placeholder={t("SEARCH_PLACEHOLDER")}
              onChange={handleChange}
              value={search}
              endIcon
            />
          </Box>

          <VaccineCodeAccordion
            t={t}
            qrcodeStatus={qrcodeStatus}
            setQrcodeStatus={setQrcodeStatus}
            vaccinationCampaignsData={vaccinationCampaignsData}
          />

          {userEnterpriseInfo.length > 0 && clinicVaccines.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                marginTop: 15,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  paddingBottom: 20,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginTop: 15,
                    paddingLeft: 21,
                    paddingRight: 21,
                  }}
                >
                  <FaCalendarAlt
                    size={20}
                    style={{ margin: "auto 0", paddingRight: 10 }}
                  />
                  <p className="montserrat" style={{ fontSize: 15 }}>
                    Calendário vacinal seguido pela sua empresa
                  </p>
                </div>
                <VaccineTable
                  search={search}
                  vaccines={userVaccines}
                  birthday={birthday}
                  type="vaccine"
                />
              </div>
              <Divider />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  paddingBottom: 30,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    paddingLeft: 21,
                    paddingRight: 21,
                    textAlign: "center",
                  }}
                >
                  <FaCalendarAlt
                    size={20}
                    style={{ margin: "auto 0", paddingRight: 10 }}
                  />
                  <p
                    className="montserrat"
                    style={{ fontSize: 15, textAlign: "center" }}
                  >
                    Calendário vacinal geral
                  </p>
                </div>
                <VaccineTable
                  search={search}
                  vaccines={clinicVaccines}
                  birthday={birthday}
                  type="vaccine"
                  applicationDateOnly
                />
              </div>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                marginTop: 15,
                paddingBottom: 30,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  marginTop: 15,
                  paddingLeft: 21,
                  paddingRight: 21,
                }}
              >
                <FaCalendarAlt
                  size={20}
                  style={{ margin: "auto 0", paddingRight: 10 }}
                />
                <p className="montserrat" style={{ fontSize: 15 }}>
                  {!userEnterpriseInfo.length
                    ? "Calendário vacinal geral"
                    : "Calendário vacinal seguido pela sua empresa"}
                </p>
              </div>
              <VaccineTable
                search={search}
                vaccines={userVaccines}
                birthday={birthday}
                type="vaccine"
                applicationDateOnly={!userEnterpriseInfo.length}
              />
            </div>
          )}

          {((userAntibodies || []).length > 0 ||
            (optionalVaccines || []).length > 0) && (
            <VaccineTable
              search={search}
              vaccines={[...optionalVaccines, ...userAntibodies]}
              birthday={birthday}
              type="antibody"
            />
          )}
        </>
      )}

      <Modal
        id="no-administrated-vaccines"
        open={isOpenNoVacAdministratedModal}
        title={t("MODAL_TITLE")}
        description={t("MODAL_DESCRIPTION")}
        leftButtonTitle={t("MODAL_BACK_BUTTON")}
        rightButtonTitle={t("MODAL_CONFIRM_BUTTON")}
        onClickLeftButton={handleCloseModal}
        onClickRightButton={handleClickConfirmModal}
      />
    </Container>
  );
};

export default MyCard;
